import { Workflow } from "@/types/Workflow";
import {
  getWorkflows as getWorkflowsApi,
  getWorkflow as getWorkflowApi,
  createWorkflow as createWorkflowApi,
  updateWorkflow as updateWorkflowApi,
  deleteWorkflow as deleteWorkflowApi,
  bulkDeleteWorkflows as bulkDeleteWorkflowsApi,
  bulkDuplicateWorkflows as bulkDuplicateWorkflowsApi,
  previewRecipients as previewRecipientsApi,
  type RecipientsPreviewRequest,
  type RecipientsPreviewResponse,
} from "@/lib/api/clients/WorkflowClient";
import { handleErrorServer, ServerError } from "@/lib/api/errors/ErrorHandler";
import { isValidObjectId } from "@/lib/utils/input-validator";

export async function getWorkflows(
  eventId: string,
): Promise<Workflow[] | ServerError> {
  try {
    if (!isValidObjectId(eventId)) {
      throw new Error("Invalid event ID.");
    }
    return await getWorkflowsApi(eventId);
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function getWorkflow(
  eventId: string,
  pipeId: string,
): Promise<Workflow | ServerError> {
  try {
    if (!isValidObjectId(eventId) || !isValidObjectId(pipeId)) {
      throw new Error("Invalid ID.");
    }
    return await getWorkflowApi(eventId, pipeId);
  } catch (error) {
    return handleErrorServer(error);
  }
}

/** `aiContentTool`: the AI lane behind the draft (J3) — see the client. */
export async function createWorkflow(
  eventId: string,
  pipe: Partial<Workflow>,
  aiContentTool?: string,
): Promise<Workflow | ServerError> {
  try {
    if (!isValidObjectId(eventId)) {
      throw new Error("Invalid event ID.");
    }
    return await createWorkflowApi(eventId, pipe, aiContentTool);
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function updateWorkflow(
  eventId: string,
  pipeId: string,
  pipe: Partial<Workflow>,
  opts: { expectedUpdatedAt?: string; aiContentTool?: string } = {},
): Promise<void | ServerError> {
  try {
    if (!isValidObjectId(eventId) || !isValidObjectId(pipeId)) {
      throw new Error("Invalid ID.");
    }
    return await updateWorkflowApi(eventId, pipeId, pipe, opts);
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function deleteWorkflow(
  eventId: string,
  pipeId: string,
): Promise<void | ServerError> {
  try {
    if (!isValidObjectId(eventId) || !isValidObjectId(pipeId)) {
      throw new Error("Invalid ID.");
    }
    return await deleteWorkflowApi(eventId, pipeId);
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function bulkDeleteWorkflows(
  eventId: string,
  workflowIds: string[],
): Promise<void | ServerError> {
  try {
    if (!isValidObjectId(eventId)) {
      throw new Error("Invalid event ID.");
    }
    return await bulkDeleteWorkflowsApi(eventId, workflowIds);
  } catch (error) {
    return handleErrorServer(error);
  }
}

export async function bulkDuplicateWorkflows(
  eventId: string,
  workflowIds: string[],
): Promise<Workflow[] | ServerError> {
  try {
    if (!isValidObjectId(eventId)) {
      throw new Error("Invalid event ID.");
    }
    return await bulkDuplicateWorkflowsApi(eventId, workflowIds);
  } catch (error) {
    return handleErrorServer(error);
  }
}

/** W4-b: the recipients preview; a ServerError (403 / 404 / 400 / network) comes back as such — never as a count. */
export async function previewRecipients(
  eventId: string,
  body: RecipientsPreviewRequest,
): Promise<RecipientsPreviewResponse | ServerError> {
  try {
    if (!isValidObjectId(eventId)) {
      throw new Error("Invalid event ID.");
    }
    return await previewRecipientsApi(eventId, body);
  } catch (error) {
    return handleErrorServer(error);
  }
}
